import { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Image, Play, Video } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase, supabaseEh, SUPABASE_URL } from '@/lib/supabase';
import { getDisplayName, getMediaUrl } from '@/lib/utils';
import { MediaRequestMetadata } from '@/lib/types';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useVideo } from '@/hooks/useVideo';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';
import { MediaCarouselDialog } from '@/components/MediaCarouselDialog';
import { EhUpgradeDialog } from '@/components/EhUpgradeDialog';
import { EhGemsDialog } from '@/components/EhGemsDialog';
import { Database } from '@/types/database';

interface GalleryItem {
  id: string;
  mediaDescription?: string;
  mediaPath: string;
  mediaType: string;
  mrMetadata: MediaRequestMetadata;
  createdAt: string;
  characterId?: string;
  characterSlug?: string;
  characterName?: string;
  characterImage?: string;
  isTooOldForFreeAccounts?: boolean;
  isVideo: boolean;
  videoStartImageUrl?: string;
}

interface CharacterMediaGroup {
  characterId: string;
  characterSlug: string;
  characterName: string;
  characterImage: string;
  mediaCount: number;
  items: GalleryItem[];
}

type MediaRequest = Database['eh']['Tables']['media_requests']['Row'];

const SKELETON_COUNT = 12;

export default function GalleryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { characterId } = useParams<{ characterId?: string; characterSlug?: string }>();
  const { characters, chats, user, is18Confirmed, isSubscriber, isUserDetailsLoading } = useUserInfo();
  const { handleRequestVideo } = useVideo();

  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Add video generation state
  const [videoStatus, setVideoStatus] = useState<Record<string, 'idle'|'pending'|'success'|'failed'>>({});
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showGemsDialog, setShowGemsDialog] = useState(false);
  const [upgradeLimitType, setUpgradeLimitType] = useState<'video' | 'galleryHistory'>('video');

  const itemsRef = useRef<GalleryItem[]>([]);

  // Fetch media requests for the last year
  const { data: mediaRequests, isLoading: isLoadingMediaRequests } = useQuery({
    queryKey: ['gallery', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const fiveYearsAgo = new Date(Date.now() - 5 * 365 * 24 * 60 * 60 * 1000).toISOString();
      
      const { data, error } = await supabaseEh
        .from('media_requests')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'success')
        .in('media_type', ['image', 'video', 'chat_image', 'chat_video'])
        .gte('created_at', fiveYearsAgo)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        if (import.meta.env.DEV) console.error('[DEV] Error fetching media requests:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user?.id,
  });

  // Process media requests into gallery items
  const galleryItems = useMemo(() => {
    if (!mediaRequests || !characters) return [];

    return mediaRequests
      .filter(mr => {
        // Filter out chat media with deleted chats
        if ((mr.media_type === 'chat_image' || mr.media_type === 'chat_video') && !mr.chat_id) {
          return false;
        }
        return true;
      })
      .map(mr => {
        const mrMetadata = mr.mr_metadata as MediaRequestMetadata;
        const chatId = mr.chat_id as string;
        let characterId = mrMetadata?.character_id as string;
        let character = characters.find(c => c.id === characterId);

        if (!character && !characterId && chatId) {
          const chat = chats.find(c => c.id === chatId);
          if (chat) {
            character = characters.find(c => c.id === chat.character_id);
            characterId = character?.id ?? '';
          }
        }
        
        let mediaPath = '';
        let isVideo = false;
        let videoStartImageUrl: string | undefined;

        if (mr.media_type.includes('video')) {
          isVideo = true;
          mediaPath = mrMetadata?.path as string;
          videoStartImageUrl = mrMetadata?.video_start_image_url as string;
        } else {
          mediaPath = mrMetadata?.path as string;
          // build path manually for exsting studio images
          if (!mediaPath && mr.media_type === 'image' && user?.id) {
            mediaPath = `publicm/i/${user.id}/EH_${mr.request_id}_${mr.resource_id}.webp`
          }
          isVideo = false;
        }

        const oneYearAgo = new Date().getTime() - 366 * 24 * 60 * 60 * 1000;

        const item: GalleryItem = {
          id: mr.id,
          mediaDescription: mrMetadata?.video_prompt || mrMetadata?.img_description || mrMetadata?.img_prompt || '',
          mediaPath,
          mediaType: mr.media_type,
          mrMetadata,
          createdAt: mr.created_at,
          isTooOldForFreeAccounts: new Date(mr.created_at).getTime() < oneYearAgo,
          characterId,
          characterSlug: character?.url_slug,
          characterName: character ? getDisplayName(character) : undefined,
          characterImage: character?.profile_media_fpaths[0] || '',
          isVideo,
          videoStartImageUrl,
        };
        return item;
      })
      .filter(item => item.mediaPath); // Filter out items without valid media paths
  }, [mediaRequests, characters, chats, user?.id]);

  // Group items by character for chat media
  const characterMediaGroups = useMemo(() => {
    const chatMedia = galleryItems.filter(item => 
      item.characterId && (item.mediaType === 'chat_image' || item.mediaType === 'chat_video')
    );

    const groups: Record<string, CharacterMediaGroup> = {};
    
    chatMedia.forEach(item => {
      if (!item.characterId || !item.characterSlug || !item.characterName) return;
      
      if (!groups[item.characterId]) {
        groups[item.characterId] = {
          characterId: item.characterId,
          characterSlug: item.characterSlug,
          characterName: item.characterName,
          characterImage: item.characterImage || '',
          mediaCount: 0,
          items: [],
        };
      }
      
      groups[item.characterId].items.push(item);
      groups[item.characterId].mediaCount++;
    });

    return Object.values(groups).sort((a, b) => b.mediaCount - a.mediaCount);
  }, [galleryItems]);

  // Studio images (non-chat media)
  const studioMedia = useMemo(() => {
    return galleryItems.filter(item => 
      ['image', 'video'].includes(item.mediaType) && !item.characterId
    );
  }, [galleryItems]);

  // Character-specific media (when viewing a specific character)
  const characterMedia = useMemo(() => {
    if (!characterId) return [];
    return galleryItems.filter(item => item.characterId === characterId);
  }, [galleryItems, characterId]);

  // Current items to display
  const currentItems = characterId ? characterMedia : [...characterMediaGroups.flatMap(g => g.items), ...studioMedia];
  itemsRef.current = currentItems;

  const handleItemClick = (item: GalleryItem) => {
    if (!isUserDetailsLoading && !isSubscriber && item.isTooOldForFreeAccounts) {
      setUpgradeLimitType('galleryHistory');
      setShowUpgradeDialog(true);
      return;
    }
    setSelectedItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteMedia = async (id: string) => {
    if (!window.confirm('Delete this media? This cannot be undone.')) return;
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) return;
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/eh-media-request`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      });
      if (!resp.ok) {
        if (import.meta.env.DEV) console.error('[DEV] Failed to delete media', await resp.text());
        return;
      }
      // Optimistically remove from query cache and local state
      queryClient.setQueryData(['gallery', user?.id], (old: MediaRequest[] = []) => old.filter(mr => mr.id !== id));
      if (selectedItem?.id === id) setIsDialogOpen(false);
    } catch (e) {
      if (import.meta.env.DEV) console.error('[DEV] Exception deleting media', e);
    }
  };

  const openCarouselAtId = (id: string) => {
    const item = itemsRef.current?.find(item => item.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDialogOpen(true);
    }
  }

  const handleBackToGallery = () => {
    navigate('/gallery');
  };

  const handleCharacterClick = ({ characterSlug, characterId }: { characterSlug: string; characterId: string }) => {
    navigate(`/gallery/${characterId}/${characterSlug}`);
  };

  // Update the video generation function to use the hook
  const handleGalleryVideoRequest = async (item: GalleryItem) => {
    if (videoStatus[item.id] === 'pending') return;
    
    setVideoStatus(prev => ({ ...prev, [item.id]: 'pending' }));

    const result = await handleRequestVideo({
      imageUrl: getMediaUrl(item.mediaPath),
      resourceId: item.id,
      mediaType: 'video',
      characterId: item.characterId,
      mrOptions: {
        safeMode: !is18Confirmed || item.mrMetadata?.safe_mode, // prevent explicit video from safe image
        style: item.mrMetadata?.style,
        videoImgDescription: item.mrMetadata?.img_description,
        videoImgPrompt: item.mrMetadata?.img_prompt || '',
      }
    });

    if (result.success) {
      setVideoStatus(prev => ({ ...prev, [item.id]: 'success' }));
      
      if (import.meta.env.DEV) console.log('[DEV] Video generated successfully:', result.data);
      if (result.data?.mrId) {
        const { data: mediaRequest } = await supabaseEh
          .from('media_requests')
          .select('*')
          .eq('id', result.data.mrId)
          .single();

        if (mediaRequest) {
          // optimistic update to avoid loading all media again
          queryClient.setQueryData(['gallery', user?.id], (old: MediaRequest[]) => [mediaRequest, ...old]);
          setTimeout(() => openCarouselAtId(mediaRequest.id), 1000);
        }
      }
    } else if (result.data?.error === 'upgrade_required') {
      setShowGemsDialog(true);
      // setUpgradeLimitType('video');
      // setShowUpgradeDialog(true);
      setVideoStatus(prev => ({ ...prev, [item.id]: 'idle' }));
    } else if (result.data?.error === 'low_gem_balance') {
      setShowGemsDialog(true);
      setVideoStatus(prev => ({ ...prev, [item.id]: 'idle' }));
    } else {
      setVideoStatus(prev => ({ ...prev, [item.id]: 'failed' }));
    }
  };

  const isLoading = isLoadingMediaRequests || isUserDetailsLoading;

  if (isLoading) {
    return (
      <div className="flex-1 lg:container max-w-6xl mx-auto px-4 py-4 md:py-8 max-lg:mb-16">
        <div className="text-center mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Gallery</h1>
          <p className="text-zinc-400">
            <span>Enjoy all your images and videos from chats or </span>
            <Link to="/studio" className="text-amber-500 font-bold hover:underline">Studio</Link>
            <span> in one place!</span>
          </p>
        </div>
        <SEO title="Gallery" description="Your AI-generated media gallery" />
        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="relative bg-zinc-900 rounded-2xl shadow-lg overflow-hidden flex flex-col h-full">
              <Skeleton className="w-full h-full min-h-72 aspect-[3/4]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 lg:container max-w-6xl mx-auto px-4 py-4 md:py-8 max-lg:mb-16">
      {!characterId && (
        <div className="text-center mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Gallery</h1>
          {currentItems.length > 0 && (
            <p className="text-zinc-400">
              <span>Enjoy all your images and videos from chats or </span>
              <Link to="/studio" className="text-amber-500 font-bold hover:underline">Studio</Link>
              <span> in one place!</span>
            </p>
          )}
        </div>
      )}
      <SEO title="Gallery" description="Your AI-generated media gallery" />
      
      {/* Header */}
      {!!characterId && (
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              onClick={handleBackToGallery}
              className="text-white hover:bg-zinc-800"
              aria-label="Back to gallery"
            >
              <ChevronLeft className="size-6" />
            </Button>
            <h1 className="text-2xl font-bold text-white">{getDisplayName(characters.find(c => c.id === characterId) ?? {})}</h1>
          </div>
        </div>
      )}

      {/* Character Media Groups */}
      {!characterId && characterMediaGroups.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">Chat Images & Videos</h2>
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
            {characterMediaGroups.map((group) => (
              <div
                key={group.characterId}
                className="relative bg-zinc-900 rounded-2xl shadow-lg overflow-hidden cursor-pointer group"
                onClick={() => handleCharacterClick({ characterSlug: group.characterSlug, characterId: group.characterId })}
              >
                {/* Character Profile Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  {group.characterImage ? (
                    <img
                      src={getMediaUrl(group.characterImage)}
                      alt={group.characterName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <Image className="w-16 h-16 text-zinc-600" />
                    </div>
                  )}
                  
                  {/* Media Count Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <div className="text-white">
                      <div className="text-xl md:text-2xl font-semibold">{group.characterName}</div>
                      <div className="text-md text-zinc-300">{group.mediaCount} media</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Studio Images */}
      {!characterId && studioMedia.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">Studio</h2>
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
            {studioMedia.map((item) => (
              <GalleryItem
                key={item.id}
                item={item}
                onClick={() => handleItemClick(item)}
                onRequestVideo={handleGalleryVideoRequest}
                onResetVideoStatus={() => setVideoStatus(prev => ({ ...prev, [item.id]: 'idle' }))}
                videoStatus={videoStatus[item.id] || 'idle'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Current Items Grid (either all or character-specific) */}
      {characterId && currentItems.length > 0 && (
        <div>
          {/* {!characterId && (
            <h2 className="text-xl font-semibold text-white mb-4">
              All Images & Videos
            </h2>
          )} */}
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
            {currentItems.map((item) => (
              <GalleryItem
                key={item.id}
                item={item}
                onClick={() => handleItemClick(item)}
                onRequestVideo={handleGalleryVideoRequest}
                onResetVideoStatus={() => setVideoStatus(prev => ({ ...prev, [item.id]: 'idle' }))}
                videoStatus={videoStatus[item.id] || 'idle'}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {currentItems.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Image className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No media yet</h3>
          <p className="text-zinc-400">
            <span>Start chatting with characters or use the </span>
            <Link to="/studio" className="text-amber-500 font-bold hover:underline">Studio</Link>
            <span> to generate your first images!</span>
          </p>
        </div>
      )}

      {/* Media Dialog */}
        {selectedItem && (
          <MediaCarouselDialog
            media={currentItems}
            isOpen={isDialogOpen}
            initialIndex={currentItems.findIndex(item => item.id === selectedItem?.id)}
            onClose={() => setIsDialogOpen(false)}
            onDelete={handleDeleteMedia}
          />
        )}

      {/* Upgrade Dialog */}
      <EhUpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        limitType={upgradeLimitType}
      />

      {/* Gems Dialog */}
      <EhGemsDialog
        open={showGemsDialog}
        onOpenChange={setShowGemsDialog}
        action="video"
      />
    </div>
  );
}

// Gallery Item Component
function GalleryItem({ item, onClick, onRequestVideo, onResetVideoStatus, videoStatus }: { 
  item: GalleryItem; 
  onClick: () => void;
  onRequestVideo?: (item: GalleryItem) => Promise<void>;
  onResetVideoStatus?: (item: GalleryItem) => void;
  videoStatus: 'idle'|'pending'|'success'|'failed';
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const blurred = item.isTooOldForFreeAccounts;

  const displayImage = item.isVideo && item.videoStartImageUrl 
    ? item.videoStartImageUrl 
    : item.mediaPath;

  return (
    <div
      className="relative bg-zinc-900 rounded-2xl shadow-lg overflow-hidden cursor-pointer group aspect-[3/4]"
      onClick={onClick}
    >
      {!imageLoaded && !imageError && (
        <Skeleton className="absolute inset-0 w-full h-full" />
      )}
      
      {imageError ? (
        <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
          <Image className="w-16 h-16 text-zinc-600" />
        </div>
      ) : (
        <img
          src={getMediaUrl(displayImage)}
          alt={`${item.characterName ?? 'Generated media'}: ${item.mediaDescription}`}
          className={`w-full h-full object-cover transition-opacity duration-200 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          } ${
            blurred ? 'blur-md' : '' 
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {/* Video Play Overlay */}
      {item.isVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-80 group-hover:opacity-100 transition-opacity">
          <div className="bg-white/90 rounded-full p-4">
            <Play className="size-8 text-zinc-800 fill-zinc-800" />
          </div>
        </div>
      )}

      {/* Video Generation Loading/Error Overlay */}
      {videoStatus === 'pending' && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-zinc-800/60 rounded-lg p-4 text-center">
            <div className="w-6 h-6 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-white text-xs">Generating video...</p>
          </div>
        </div>
      )}
      {videoStatus === 'failed' && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="bg-zinc-800/80 rounded-lg p-4 text-center">
            <p className="text-red-400 mb-2 text-xs">Video generation failed</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResetVideoStatus?.(item);
              }}
              className="px-3 py-1 bg-pink-600 text-white rounded-sm hover:bg-pink-500 transition-colors text-xs"
              type="button"
              >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Video Generation Button (only for images) */}
      {!item.isVideo && (
        <div className="absolute bottom-3 right-3">
          <Button
            variant="outline"
            className={`h-auto py-1 border-none bg-black/50 hover:bg-pink-600/60 text-white hover:text-white ${
              videoStatus === 'pending' ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent opening the carousel
              if (onRequestVideo && videoStatus !== 'pending') {
                onRequestVideo(item);
              }
            }}
            disabled={videoStatus === 'pending'}
          >
            {videoStatus === 'pending' ? (
              <span className="size-4 border-2 border-zinc-600 border-t-pink-500 rounded-full animate-spin"></span>
            ) : videoStatus === 'failed' ? (
              <span className="text-xs text-red-400">Retry</span>
            ) : (
              <>
                <Video className="size-5" />
                <span className="hidden sm:inline-block text-sm">Video</span>
                <span className="text-xs -mx-1">💎</span>
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
